<template>
  <div
    id="app"
  >
    <loading-banner
      v-if="refreshing"
      title="Loading..."
    >
      <transition-group
        slot="subtitle"
        class="owal-loading__items"
        tag="ul"
        name="list"
      >
        <li
          v-for="service in humanReadableRefreshState"
          :key="service"
        >
          {{ service }}
        </li>
      </transition-group>
    </loading-banner>

    <navbar />
    <div
      id="content"
    >
      <owal-splitview-layout
        v-if="splitView"
        :is-authenticated="isAuthenticated"
      >
        <router-view
          slot="master"
          name="master"
        />
        <router-view
          slot="detail"
          name="detail"
        />
      </owal-splitview-layout>
      <div
        v-else
        class="full-width"
      >
        <router-view />
      </div>
    </div>
  </div>
</template>

<script>
import navbar from '@/components/shared/NavigationBar'
import LoadingBanner from '@/components/shared/LoadingBanner'
import { mapGetters, mapActions } from 'vuex'
import axios from 'axios'

import { SVC_A, SVC_C, SVC_C_RT, SVC_D } from '@/libraries/api/base'
// Import tailwindcss styles
import '@/assets/css/tailwind.css'

const OwalSplitviewLayout = () => import(/* webpackPreload: true */'@/libraries/owal-ui').then(module => { return module.OwalSplitviewLayout })
/**
 * This next line is the same as:

import { chime } from './libraries/util/chime'
exports.chime = chime

Exporting "uses" the imported module, so we silence eslint warnings
about unused imports.

 */
export { chime } from './libraries/util/chime'

export default {
  name: 'App',
  data () {
    return {
      searchText: undefined,
      loadingSubtitle: [''],
    }
  },
  created () {
    // console.debug(`Checkpoint: App.vue -> created() called.`)
    this.applyAuthTokenToAllRequests()
    this.observeNavigationChanges()
    this.updateTitleForDevelopment()
  },
  mounted () {
    // console.debug(`Checkpoint: App.vue -> mounted() called.`)
    this.trackNetworkFailures()
    if (this.isAuthenticated) {
      this.loadUserData()
      console.log('loaded user data')
    }
  },
  computed: {
    ...mapGetters({
      isAuthenticated: 'isAuthenticated',
      token: 'authToken',
      currentDomainUUID: 'currentDomainUUID',
      refreshing: 'refreshing',
      humanReadableRefreshState: 'humanReadableRefreshState',
    }),
    splitView: function () {
      const useSplitView =
        this.$route.matched &&
        this.$route.matched.length &&
        Object.keys(this.$route.matched[0].components).length > 1

      return useSplitView
    },
    redirectPath: function () {
      if (!this.$route) {
        return
      }

      if (!this.$route.query) {
        return
      }

      if (!this.$route.query.redirect) {
        return
      }

      return this.$route.query.redirect
    },
  },
  methods: {
    ...mapActions(['refreshDataModel']),
    trackNetworkFailures () {
      axios.interceptors.response.use(null, this.logError)
    },
    applyAuthTokenToAllRequests () {
      axios.interceptors.request.use(this.applyToken)
    },
    observeNavigationChanges () {
      // console.debug(`Checkpoint: App.vue -> observeNavigation() called.`)
      this.$store.subscribe((mutation, state) => {
        const mutationType = mutation.type
        const isAuthenticationChange =
          ['finishLogin', 'logout'].indexOf(mutationType) > -1
        const isDataModelDownloadCompletion = mutationType === 'cacheUserData'
        const isEntitlementsChange = mutationType === 'setUserPermissions'
        // console.log(`Mutation type '${mutationType}' ${isAuthenticationChange ? 'is' : 'is not'} an authentication change.`)
        // console.debug(`Checkpoint: App.vue -> mutation observer called with mutation type ${mutationType}.`)

        if (isAuthenticationChange) {
          const userId = this.$store.state.email
          if (this.$ga) {
            this.$ga.set({ userId })
          }
          this.loadUserData()
          this.redirectBasedOnAuthenticationState()
        }
        if (isDataModelDownloadCompletion || isEntitlementsChange) {
          // console.log('Got user permissions in store...')
          // TODO: Change this to general case of deep link.
          const shouldRedirect = !!this.redirectPath
          if (shouldRedirect) {
            this.redirectBasedOnAuthenticationState()
          }
        }
      })
    },
    updateTitleForDevelopment () {
      if (location.hostname === 'localhost') {
        if (!document.title.startsWith('🔨')) {
          document.title = `🔨 ${document.title}`
        }
      }
    },
    applyToken (config) {
      const AUTHORIZED_URLS_REGEXP = /https:\/\/(qa.|)(svc-[a-z].)?api.owal.io\/.*|http:\/\/localhost[:0-9]*\/.*/
      const services =
        [SVC_A, SVC_C, SVC_C_RT, SVC_D].reduce((urls, svc, index) => {
          urls.push(svc.prodUri)
          urls.push(svc.debugUri)
          urls.push(`${svc.prodUri.slice(0, 7)}qa.${svc.prodUri.slice(8)}`)
          return urls
        }, []) || []
      const isExactMatch = !!services.find(scvUrl =>
        config.url.startsWith(scvUrl),
      )
      // console.log('Services: ', services, ' isExact: ', isExactMatch)

      if (!AUTHORIZED_URLS_REGEXP.test(config.url) && !isExactMatch) {
        return config
      }

      const method = config.method
      config.headers[method].authorization = `Bearer ${this.token}`

      if (config.data) {
        config.data['token'] = this.token
        // console.debug(`${method} request has data.`)
      }

      // console.debug(`Applying token to ${method} request.`)
      return config
    },
    logError (error) {
      this.$ga.event({
        eventCategory: `axios-network-error`,
        eventAction: 'error',
        eventLabel: JSON.stringify({
          message: error.message,
        }),
        eventValue: error.status,
      })
      return Promise.reject(error)
    },
    redirectBasedOnAuthenticationState () {
      // console.log(`Redirecting because user is${this.isAuthenticated ? '' : ' not'} authenticated.`)
      if (this.isAuthenticated) {
        let nextPath = this.redirectPath
        const isEmpty =
          this.$route.path === '/' || !this.$route || !this.$route.path
        if (!nextPath && isEmpty) {
          nextPath = '/dashboard'
        }
        // console.debug(`Next path: ${nextPath}`)
        if (nextPath) {
          this.$router.push({
            path: nextPath,
          })
        }
      } else {
        // console.debug(`Next path: /`)
        this.$router.push({ path: '/', query: this.$route.query })
      }
    },
    loadUserData () {
      if (this.isAuthenticated) {
        return this.refreshDataModel().then(
          this.redirectBasedOnAuthenticationState,
        )
      }
      // The else case is handled by the store's cleanup on logout. We simply clear the cache.
    },
  },
  components: {
    navbar,
    OwalSplitviewLayout,
    LoadingBanner,
  },
}
</script>

<style>
@import '../node_modules/text-security/dist/text-security.css';

html,
body {
  display: flex;
  flex-grow: 100;
  height: 100%;
  width: 100%;
  margin: 0;
  padding: 0;
}

.full-width {
  width: 100%;
}

#content {
  display: flex;
  flex-grow: 100;
  align-content: center;
  justify-content: center;
  padding: 0;
  margin: 0;
  padding-top: 44px;
  height: 100%;
  width: 100%;
  overflow: hidden;
}

#app {
  font-family: 'Lato', 'Avenir', 'Helvetica', 'Arial', sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #2c3e50;
  height: 100%;
  width: 100%;
  margin: 0;
  padding: 0;
  background-color: #324057;
  /* background-color: rgb(50, 70, 90); */
  background-size: cover;
}

.owal-loading__items {
  margin: 0;
  padding: 0;
  list-style-type: none;
  overflow: auto;
  transition: all 300ms;
}

.list-enter-active,
.list-leave-active {
  transition: all 1s;
  color: #aaa;
}
.list-enter, .list-leave-to /* .list-leave-active below version 2.1.8 */ {
  opacity: 0;
  transform: translateX(-3rem);
  color: #239c23;
}
</style>
